import { useEffect, useMemo, useState } from "react";

import { useAuth } from "~/auth/useAuth";
import { useConfirmModal } from "~/components/common/confirm-modal/ConfirmModalContext";
import { useRouteParams } from "~/data/route";
import {
	useDocumentWorkspaces,
	useOwnedWorkspaces,
	usePersonalWorkspace,
	useTransferSyncState,
} from "~/state";
import { LOCAL_WORKSPACE_NAME, type Workspace } from "~/state/types";
import { Button } from "~/ui/Button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "~/ui/Card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~/ui/Select";

const useConfirmTransfer = () => {
	const { confirm } = useConfirmModal();

	return (workspaceName: string) => {
		return confirm({
			headingTitle: "Transfer Document",
			content: (
				<>
					Are you sure you want to transfer this document to{" "}
					<strong>{workspaceName}</strong>?
					<br />
					<span className="text-destructive">
						For now, you cannot undo this and must delete the document to reverse the
						transfer.
					</span>
				</>
			),
			variant: "normal",
			confirmLabel: "Transfer",
		});
	};
};

export const TransferTab = () => {
	const { documentId } = useRouteParams();
	const { user: loggedInUser } = useAuth();
	const confirmTransfer = useConfirmTransfer();

	const { isLoading, isError, errorState, attachWorkspace } = useTransferSyncState(documentId);

	const w = useDocumentWorkspaces(documentId);
	const availableWorkspaces = useOwnedWorkspaces();
	const personalWorkspace = usePersonalWorkspace(loggedInUser);
	const [remoteRepoExists, setRemoteRepoExists] = useState<boolean>(false);
	const [selectedWorkspace, setSelectedWorkspace] = useState<string>(
		personalWorkspace?.name || ""
	);

	const remoteWorkspaces = useMemo(() => {
		const map = new Map<string, Workspace>();
		w?.forEach((workspace) => {
			if (workspace.name !== LOCAL_WORKSPACE_NAME) {
				map.set(workspace.id, workspace);
			}
		});
		return Array.from(map.values());
	}, [w]);

	useEffect(() => {
		const doesNotExist = remoteWorkspaces === undefined || remoteWorkspaces?.length === 0;
		setRemoteRepoExists(!doesNotExist);
	}, [remoteWorkspaces, setRemoteRepoExists]);

	return (
		<Card>
			<CardHeader className="pb-3">
				<CardTitle>Transfer this document [INTERNAL USE ONLY]</CardTitle>
				{remoteWorkspaces?.length === 0 && personalWorkspace && (
					<CardDescription>
						Transfer a local document to a workspace so it can be synced to the cloud.
						Until we figure out multiple remotes, this only supports transferring to a
						single workspace.
					</CardDescription>
				)}
			</CardHeader>
			{!remoteRepoExists && (
				<CardContent>
					<div className="flex flex-col gap-4">
						<Select
							defaultValue={selectedWorkspace}
							onValueChange={(value) => {
								setSelectedWorkspace(value);
							}}
						>
							<SelectTrigger className="ml-auto" aria-label="Select workspace">
								<SelectValue />
							</SelectTrigger>

							<SelectContent>
								{availableWorkspaces?.map((workspace) => (
									<SelectItem value={workspace.name} key={workspace.id}>
										{workspace.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<Button
							className="-mb-1"
							preset="primary"
							loading={isLoading}
							onClick={async () => {
								const confirmStatus = await confirmTransfer(selectedWorkspace);
								if (confirmStatus) {
									await attachWorkspace(selectedWorkspace);
									setRemoteRepoExists(true);
								}
							}}
						>
							Transfer Document
						</Button>
					</div>
				</CardContent>
			)}
			{isError && (
				<CardContent>
					<div className="text-destructive">
						<p className="font-semibold">
							Error transferring document to {selectedWorkspace}
							{errorState?.message && `: ${errorState.message}`}
						</p>
					</div>
				</CardContent>
			)}
			{remoteRepoExists && (
				<CardContent>
					<div>
						This document is already associated with workspace.
						<ul>
							{remoteWorkspaces?.map((workspace) => (
								<li key={workspace.id}>
									<span className="font-semibold">{workspace.name}</span> (
									<code className="text-xs text-secondary">
										{workspace.host}/git/{workspace.slug}
									</code>
									)
								</li>
							))}
						</ul>
					</div>
				</CardContent>
			)}
		</Card>
	);
};
